import React, {Suspense, useContext, useEffect, useState} from 'react';
import {Navigate, Route, Routes} from "react-router-dom";
import {ThemeProvider} from "@mui/material";
import {theme} from "./config/Theme";
import 'react-toastify/dist/ReactToastify.css';
import {ToastContainer} from "react-toastify";
import LoadingScreen from "./components/loading-screen/LoadingScreen";
import {LoginPage} from "./pages/auth/login/LoginPage";
import {Projects} from "./pages/projects/Projects";
import {SignInContext} from "./context/SignInContext";
import {ReactFlowProvider} from "reactflow";
import UnderMaintenance from "./pages/under-maintenance/UnderMaintenance";
import {ProjectHomePage} from "./pages/project/ProjectHomePage";
import {RegisterPage} from "./pages/auth/register/RegisterPage";
import {RegistryOrganizationsPage} from "./pages/organizations/RegistryOrganizationsPage";
import {RegistryAiModelsPage} from "./pages/ai-models/RegistryAiModelsPage";
import {ApiKeys} from "./pages/api-keys/ApiKeys";
import {RegistryTranscribeModelsPage} from "./pages/transcribe-models/RegistryTranscribeModelsPage";
import {RegistryVoiceModelsPage} from "./pages/voice-models/RegistryVoiceModelsPage";
import {RegistryLanguagesPage} from "./pages/languages/RegistryLanguagesPage";
import {BackendConfigurations} from "./pages/backend-configurations/BackendConfigurations";
import {registryProjectRouteElement} from "./pages/registry-project/registry-project-route-element";
import {RegistryUsersPage} from "./pages/users/RegistryUsersPage";
import {ProjectCreationPageNew} from "./pages/project-creation/ProjectCreationPageNew";
import {OAuthSuccess} from "./pages/auth/login/OAuthSuccess";
import {MainLayout} from "./layouts/main-layout/MainLayout";
import {LandingPage} from "./pages/landing/landing-page";
import {ChangePasswordPage} from "./pages/auth/change-password/ChangePasswordPage";
import {ForgotPasswordPage} from "./pages/auth/forgot-password/ForgotPassword";
import "react-datepicker/dist/react-datepicker.css";
import {ConfirmDialogModal} from "./components/modals/ConfirmDialogModal";
import {CallConfirmDialogModal} from "./components/modals/CallConfirmDialogModal";
import {WebrtcProvider} from "./context/WebrtcContext";
import isElectron from "./utils/functions";
import {ProjectElectronLayout} from "./pages/project/project-electron-layout";
import OnboardingPage from "./pages/onboarding/OnboardingPage";

export const App = () => {
  const {isAuth, currentUser} = useContext(SignInContext);
  const isAdmin = currentUser?.roles?.includes("admin");
  const isManager = currentUser?.roles?.includes("manager");
  const [isElectronApp, setIsElectronApp] = useState(null);

  useEffect(() => {
    const response = isElectron();
    setIsElectronApp(response);
  }, []);

  if (isAuth === null || isElectronApp === null) {
    return <LoadingScreen />
  }

  if (isElectronApp) {
    return (
      <ThemeProvider theme={theme}>
        <ReactFlowProvider>
          <ToastContainer theme={"light"}/>
          <Suspense fallback={<LoadingScreen/>}>
            <ConfirmDialogModal/>
            <CallConfirmDialogModal/>
            <Routes>
              <Route path="/under-maintenance" exact index element={<UnderMaintenance/>}/>

              {
                !isAuth ?
                  <>
                    <Route path="/login" index element={<LoginPage/>}/>
                    <Route path="/register" index element={<RegisterPage/>}/>
                    <Route path="/forgot-password" index element={<ForgotPasswordPage/>}/>
                    <Route path="/change-password" index element={<ChangePasswordPage/>}/>
                    <Route path="/user/reset-password" index element={<ChangePasswordPage/>}/>
                    <Route path="/oauth2/redirect" index element={<OAuthSuccess/>}/>
                    <Route path="*" element={<Navigate to="/login"/>}/>
                  </>
                  :
                  <>
                    <Route element={<MainLayout/>}>
                      <Route path="/projects" exact index element={<Projects/>}/>
                      <Route path="/registry-project/create" exact index element={<ProjectCreationPageNew/>}/>
                    </Route>
                    <Route path="/project/:projectId/supervision/:conversationId" element={<ProjectElectronLayout/>}/>
                    <Route path="/project/:projectId/supervision" element={<ProjectElectronLayout/>}/>
                    <Route path="/project/:projectId/" element={<ProjectElectronLayout/>}/>
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="*" element={<Navigate to="/projects"/>}/>
                  </>
              }
            </Routes>
          </Suspense>
        </ReactFlowProvider>
      </ThemeProvider>
    )
  }


  return (
    <ThemeProvider theme={theme}>
      <ReactFlowProvider>
        <ToastContainer theme={"light"} position={"bottom-right"}/>
        {/* the agent's browser phone (WebRTC), kept across routes */}
        <WebrtcProvider>
        <Suspense fallback={<LoadingScreen/>}>
          <ConfirmDialogModal/>
          <CallConfirmDialogModal/>
          <Routes>
            <Route path="/under-maintenance" exact index element={<UnderMaintenance/>}/>

            {
              !isAuth ?
                <>
                  {/*<Route path="/" exact index element={<LandingPage/>}/>*/}
                  <Route path="/login" index element={<LoginPage/>}/>
                  <Route path="/register" index element={<RegisterPage/>}/>
                  <Route path="/forgot-password" index element={<ForgotPasswordPage/>}/>
                  <Route path="/change-password" index element={<ChangePasswordPage/>}/>
                  <Route path="/user/reset-password" index element={<ChangePasswordPage/>}/>
                  <Route path="/oauth2/redirect" index element={<OAuthSuccess/>}/>
                  <Route path="*" element={<Navigate to="/login"/>}/>
                </>
                :
                <>
                  <Route element={<MainLayout/>}>
                    {/*<Route path="/" exact index element={<Projects/>}/>*/}
                    <Route path="/organizations" exact index element={<RegistryOrganizationsPage/>}/>
                    {isAdmin && <>
                      <Route path="/ai-models" exact index element={<RegistryAiModelsPage/>}/>
                      <Route path="/languages" exact index element={<RegistryLanguagesPage/>}/>
                    </>}
                    {/* these three also have manage controls gated on "manager" (see canManage in each page) */}
                    {(isAdmin || isManager) && <>
                      <Route path="/transcribe-models" exact index element={<RegistryTranscribeModelsPage/>}/>
                      <Route path="/voice-models" exact index element={<RegistryVoiceModelsPage/>}/>
                      <Route path="/backend-configuration" exact index element={<BackendConfigurations/>}/>
                    </>}
                    <Route path="/users" exact index element={<RegistryUsersPage/>}/>
                    <Route path="/api-keys" exact index element={<ApiKeys/>}/>
                    <Route path="/projects" exact index element={<Projects/>}/>
                    <Route path="/registry-project/create" exact index element={<ProjectCreationPageNew/>}/>
                  </Route>

                  <Route path="/registry-project/edit/:projectId" exact index element={<ProjectCreationPageNew/>}/>
                  {/*<Route path="/project/:projectId/*" element={<ProjectHomePage/>}/>*/}
                  {registryProjectRouteElement}
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route path="*" element={<Navigate to="/projects"/>}/>
                </>
            }
          </Routes>
        </Suspense>
        </WebrtcProvider>
      </ReactFlowProvider>
    </ThemeProvider>
  );
}
